import React, { useState, useEffect } from 'react';
import { TrendingUp, Info, Calculator, AlertCircle, CheckCircle, ArrowLeft, PieChart, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import SimpleKakaoButton from '../../Components/SimpleKakaoButton';
import ShareButton from './ShareButton';

const EffectCalculator = () => {
  const [inputs, setInputs] = useState({
    monthlyVisitors: '',
    currentRate: '',
    improvedRate: '',
    averageOrderValue: ''
  });

  const [result, setResult] = useState(null);
  const [showTooltip, setShowTooltip] = useState('');

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 효과 계산 함수
  const calculateEffect = () => {
    const monthlyVisitors = parseInt(inputs.monthlyVisitors) || 0;
    const currentRate = parseFloat(inputs.currentRate) / 100;
    const improvedRate = parseFloat(inputs.improvedRate) / 100;
    const aov = parseFloat(inputs.averageOrderValue) || 0;

    if (monthlyVisitors === 0 || currentRate === 0 || improvedRate === 0 || aov === 0) return null;
    if (improvedRate <= currentRate) return null;

    // 현재 매출
    const currentMonthlyRevenue = monthlyVisitors * currentRate * aov;
    const currentAnnualRevenue = currentMonthlyRevenue * 12;

    // 개선된 매출
    const improvedMonthlyRevenue = monthlyVisitors * improvedRate * aov;
    const improvedAnnualRevenue = improvedMonthlyRevenue * 12;

    // 매출 증가
    const monthlyIncrease = improvedMonthlyRevenue - currentMonthlyRevenue;
    const annualIncrease = monthlyIncrease * 12;

    // 개선율
    const improvementPercent = ((improvedRate - currentRate) / currentRate) * 100;

    return {
      currentMonthlyRevenue,
      currentAnnualRevenue,
      improvedMonthlyRevenue,
      improvedAnnualRevenue,
      monthlyIncrease,
      annualIncrease,
      improvementPercent,
      currentConversions: Math.round(monthlyVisitors * currentRate),
      improvedConversions: Math.round(monthlyVisitors * improvedRate),
      additionalConversions: Math.round(monthlyVisitors * (improvedRate - currentRate))
    };
  };

  // 실시간 계산
  useEffect(() => {
    const calculated = calculateEffect();
    setResult(calculated);
  }, [inputs]);

  // 입력 검증
  const isValidInput = () => {
    return inputs.monthlyVisitors &&
      inputs.currentRate &&
      inputs.improvedRate &&
      inputs.averageOrderValue &&
      parseInt(inputs.monthlyVisitors) > 0 &&
      parseFloat(inputs.currentRate) > 0 &&
      parseFloat(inputs.currentRate) < 100 &&
      parseFloat(inputs.improvedRate) > 0 &&
      parseFloat(inputs.improvedRate) < 100 &&
      parseFloat(inputs.improvedRate) > parseFloat(inputs.currentRate) &&
      parseFloat(inputs.averageOrderValue) > 0;
  };

  return (
    <div className="bg-white overflow-hidden">
      {/* 배경 장식 요소 */}
      <div className="absolute top-0 right-0 -z-10 opacity-30">
        <svg width="800" height="800" viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="400" cy="400" r="400" fill="url(#radialYellow)" fillOpacity="0.3" />
          <defs>
            <radialGradient id="radialYellow" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(400 400) rotate(90) scale(400)">
              <stop stopColor="#EAB308" />
              <stop offset="1" stopColor="#EAB308" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>

      <Header />

      {/* 히어로 섹션 */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 to-white -z-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/tools"
            className="inline-flex items-center text-yellow-600 hover:text-yellow-700 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            전체 도구로 돌아가기
          </Link>

          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium mb-6">
              <TrendingUp className="w-4 h-4 mr-2" />
              전환율 개선 효과 계산기
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-6">
              전환율 개선의 <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-600 to-amber-600">비즈니스 임팩트</span><br />
              정확하게 측정하기
            </h1>

            <p className="text-xl text-gray-600 mb-8">
              전환율 개선으로 예상되는 매출 증가와 비즈니스 임팩트를 정확히 계산합니다.<br />
              데이터 기반의 의사결정을 위한 구체적인 수치를 제공합니다.
            </p>
            <div className="flex justify-center mb-8">
              <ShareButton />
            </div>
          </div>
        </div>
      </section>

      {/* 계산기 메인 섹션 */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* 입력 폼 */}
            <div className="bg-white rounded-2xl shadow-lg border border-yellow-200 p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">📊</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">비즈니스 데이터 입력</h2>
              </div>

              <div className="space-y-6">
                {/* 월간 방문자 수 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('monthlyVisitors')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        월간 방문자 수
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'monthlyVisitors' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        한 달 동안의 웹사이트 방문자 수
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    min="0"
                    placeholder="예: 10000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    value={inputs.monthlyVisitors}
                    onChange={(e) => handleInputChange('monthlyVisitors', e.target.value)}
                  />
                </div>

                {/* 현재 전환율 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('currentRate')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        현재 전환율 (%)
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'currentRate' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        현재 웹사이트의 전환율
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="예: 2.5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    value={inputs.currentRate}
                    onChange={(e) => handleInputChange('currentRate', e.target.value)}
                  />
                </div>

                {/* 개선된 전환율 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('improvedRate')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        개선된 전환율 (%)
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'improvedRate' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        테스트 후 달성한 전환율
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="예: 3.2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    value={inputs.improvedRate}
                    onChange={(e) => handleInputChange('improvedRate', e.target.value)}
                  />
                </div>

                {/* 평균 주문 금액 */}
                <div>
                  <div className="flex items-center mb-2 relative">
                    <div
                      className="flex items-center cursor-help px-2 py-1 -mx-2 -my-1 rounded hover:bg-gray-50"
                      onMouseEnter={() => setShowTooltip('averageOrderValue')}
                      onMouseLeave={() => setShowTooltip('')}
                    >
                      <label className="block text-sm font-medium text-gray-700">
                        평균 주문 금액 (원)
                      </label>
                      <Info className="w-4 h-4 ml-2 text-gray-400" />
                    </div>
                    {showTooltip === 'averageOrderValue' && (
                      <div className="absolute z-10 left-0 top-8 p-2 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap pointer-events-none">
                        고객 한 명이 평균적으로 구매하는 금액
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    min="0"
                    placeholder="예: 50000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    value={inputs.averageOrderValue}
                    onChange={(e) => handleInputChange('averageOrderValue', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* 결과 표시 */}
            <div className="space-y-6">
              {/* 계산 결과 카드 */}
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-8 border-2 border-yellow-200">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-yellow-200 rounded-xl flex items-center justify-center mr-4">
                    <PieChart className="w-6 h-6 text-yellow-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">개선 효과 분석</h2>
                </div>

                {isValidInput() && result ? (
                  <div className="space-y-6">
                    {/* 주요 지표 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/60 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-yellow-600 mb-1">
                          +{result.improvementPercent.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-600">전환율 개선</div>
                      </div>
                      <div className="bg-white/60 rounded-xl p-4 text-center">
                        <div className="text-3xl font-bold text-yellow-600 mb-1">
                          +{result.additionalConversions.toLocaleString()}건
                        </div>
                        <div className="text-sm text-gray-600">월간 추가 전환</div>
                      </div>
                    </div>

                    {/* 매출 증가 */}
                    <div className="bg-white/60 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">매출 증가</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">월간 매출 증가</span>
                          <span className="font-medium text-green-600">+{result.monthlyIncrease.toLocaleString()}원</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">연간 매출 증가</span>
                          <span className="font-medium text-green-600">+{result.annualIncrease.toLocaleString()}원</span>
                        </div>
                      </div>
                    </div>

                    {/* 전환 증가 */}
                    <div className="bg-white/60 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">월간 전환 변화</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">기존 전환 수</span>
                          <span className="font-medium">{result.currentConversions.toLocaleString()}건</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">개선 후 전환 수</span>
                          <span className="font-medium">{result.improvedConversions.toLocaleString()}건</span>
                        </div>
                        <div className="flex justify-between border-t border-gray-200 pt-2">
                          <span className="text-gray-600">추가 전환 수</span>
                          <span className="font-medium text-green-600">+{result.additionalConversions.toLocaleString()}건</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      {!inputs.monthlyVisitors ? '월간 방문자 수를 입력해주세요' :
                        !inputs.currentRate ? '현재 전환율을 입력해주세요' :
                          !inputs.improvedRate ? '개선된 전환율을 입력해주세요' :
                            !inputs.averageOrderValue ? '평균 주문 금액을 입력해주세요' :
                              parseFloat(inputs.improvedRate) <= parseFloat(inputs.currentRate) ? '개선된 전환율은 현재 전환율보다 높아야 합니다' :
                                '올바른 값을 입력해주세요'}
                    </p>
                  </div>
                )}
              </div>

              {/* 해석 가이드 */}
              {isValidInput() && result && (
                <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-blue-600 mr-3 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">결과 해석</h3>
                      <div className="text-sm text-blue-800 space-y-2">
                        <p>
                          • 전환율 <strong>{result.improvementPercent.toFixed(1)}% 개선</strong>으로 연간 <strong>{result.annualIncrease.toLocaleString()}원</strong>의 매출 증가가 예상됩니다.
                        </p>
                        <p>
                          • 월간 <strong>{result.additionalConversions.toLocaleString()}건</strong>의 추가 전환을 통해 지속적인 수익 증대가 가능합니다.
                        </p>
                        <p>
                          • 이는 현재 대비 <strong>{((result.additionalConversions / result.currentConversions) * 100).toFixed(0)}% 증가</strong>한 전환 성과입니다.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 다른 도구 추천 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">다음 단계 도구</h2>
            <p className="text-xl text-gray-600">개선 효과 계산 후 이어서 사용할 수 있는 도구들입니다</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow-sm border-2 border-red-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">💵</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">ROI(순수익) 계산기</h3>
              </div>
              <p className="text-gray-600 mb-6">판매가와 순이익을 기반으로 실제 투자 효과를 계산합니다.</p>
              <Link
                to="/tools/roi"
                className="block w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 px-4 rounded-xl font-medium hover:shadow-md transition-all text-center"
              >
                ROI 계산하기
              </Link>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border-2 border-purple-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">⏱️</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">A/B 테스트 기간 계산기</h3>
              </div>
              <p className="text-gray-600 mb-6">다음 테스트를 위한 최적의 테스트 기간을 계산합니다.</p>
              <Link
                to="/tools/duration"
                className="block w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-3 px-4 rounded-xl font-medium hover:shadow-md transition-all text-center"
              >
                테스트 기간 계산하기
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            지금 바로 전환율 최적화를 시작하세요
          </h2>
          <p className="text-xl mb-10 text-blue-100">
            SWITCHUP으로 직관적이고 편리하게 A/B 테스트를 실행하고 전환율을 개선하세요
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-10">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              {[
                { label: "직관적인 인터페이스", description: "복잡한 설정 없이 쉽게 시작" },
                { label: "30일 무료 체험", description: "부담 없이 모든 기능 사용" },
                { label: "즉시 결과 확인", description: "실시간 데이터와 분석 제공" }
              ].map((item, index) => (
                <div key={index}>
                  <div className="text-xl font-bold mb-2">{item.label}</div>
                  <div className="text-blue-100">{item.description}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/freestart"
              className="px-8 py-4 bg-white text-blue-600 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-medium"
            >
              무료로 시작하기
            </Link>
            <Link
              to="/service/ab-test"
              className="px-8 py-4 border-2 border-white text-white rounded-xl hover:bg-white hover:text-blue-600 transition-all duration-300 font-medium"
            >
              SWITCHUP 서비스 보기
            </Link>
          </div>
        </div>
      </section>

      {/* 카카오 문의하기 버튼 */}
      {/* <SimpleKakaoButton
        channelId="_yixgxjn"
        buttonText="카카오로 문의하기"
        mobileText="카카오 문의"
      /> */}
      <Footer />
    </div>
  );
};

export default EffectCalculator;